import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Form,
  Select,
  message,
  Progress,
  Alert,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Badge,
  Divider,
  Modal,
  Radio,
  Tag,
  Table
} from 'antd';
import {
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  DatabaseOutlined,
  BookOutlined,
  TeamOutlined,
  FolderOutlined,
  UserOutlined,
  ReadOutlined,
  PlayCircleOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import courseService from '../../services/courseService';
import nganhHocService from '../../services/nganhHocService';
import * as subjectService from '../../services/subjectService';
import * as classService from '../../services/classService';
import lopHocPhanService from '../../services/lopHocPhanService';
import sinhVienService from '../../services/sinhVienService';
import type { Course, NganhHoc, Class, LopHocPhan } from '../../../../shared/types';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

interface SyncLog {
  key: string;
  timestamp: string;
  module: string;
  status: 'success' | 'error' | 'warning';
  message: string;
  details?: {
    added?: number;
    updated?: number;
    deleted?: number;
    preserved?: number;
    invalidStudents?: number;
  };
}

const DataSyncManagementPage: React.FC = () => {
  // States
  const [courses, setCourses] = useState<Course[]>([]);
  const [nganhHocs, setNganhHocs] = useState<NganhHoc[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [lopHocPhans, setLopHocPhans] = useState<LopHocPhan[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [activeTab, setActiveTab] = useState('1');

  // Forms
  const [courseForm] = Form.useForm();
  const [subjectForm] = Form.useForm();
  const [classForm] = Form.useForm();
  const [lopHocPhanForm] = Form.useForm();
  const [studentForm] = Form.useForm();
  const [studentLHPForm] = Form.useForm();
  const [bulkSyncForm] = Form.useForm();

  // Student sync mode
  const [studentSyncMode, setStudentSyncMode] = useState<'safe' | 'full'>('safe');
  const [bulkSyncMode, setBulkSyncMode] = useState<'single' | 'bulk'>('single');

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      // Load courses
      const courseRes = await courseService.getCourses({ page: 1, limit: 1000 });
      if (courseRes.success) {
        setCourses(courseRes.data);
      }

      // Load nganh hoc
      const nganhRes = await nganhHocService.getNganhHoc({ limit: 1000 });
      if (nganhRes.success) {
        setNganhHocs(nganhRes.data);
      }

      // Load classes
      const classRes = await classService.listClasses({ page: 1, limit: 1000 });
      if (classRes.data) {
        setClasses(classRes.data);
      }

      // Load lop hoc phan
      const lhpRes = await lopHocPhanService.getLopHocPhanList({ page: 1, limit: 10000 });
      if (lhpRes.success) {
        setLopHocPhans(lhpRes.data);
      }

      // Load subjects
      const subjectRes = await subjectService.listSubjects({ page: 1, limit: 10000 });
      setSubjects(subjectRes.data || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
      message.error('Không thể tải dữ liệu khởi tạo');
    }
  };

  const addLog = (module: string, status: 'success' | 'error' | 'warning', messageText: string, details?: any) => {
    const log: SyncLog = {
      key: Date.now().toString(),
      timestamp: new Date().toLocaleString('vi-VN'),
      module,
      status,
      message: messageText,
      details
    };
    setSyncLogs(prev => [log, ...prev].slice(0, 50)); // Keep last 50 logs
  };

  // Sync Khóa học
  const handleSyncCourses = async () => {
    try {
      const values = await courseForm.validateFields();
      setLoading(prev => ({ ...prev, course: true }));
      
      const response = await courseService.syncCourses(values);
      if (response.success) {
        const { added, updated } = response.data;
        addLog('Khóa học', 'success', `Đồng bộ thành công: ${added} thêm mới, ${updated} cập nhật`, { added, updated });
        message.success(`Đồng bộ khóa học thành công: ${added} thêm mới, ${updated} cập nhật`);
        loadInitialData();
      }
    } catch (error: any) {
      console.error('Error syncing courses:', error);
      const errorMsg = error.response?.data?.message || 'Không thể đồng bộ khóa học';
      addLog('Khóa học', 'error', errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, course: false }));
    }
  };

  // Sync Môn học
  const handleSyncSubjects = async () => {
    try {
      const values = await subjectForm.validateFields();
      setLoading(prev => ({ ...prev, subject: true }));
      
      const result = await subjectService.syncSubjects({ KhoaHocID: values.KhoaHocID });
      addLog('Môn học', 'success', `Đồng bộ thành công: ${result.added} môn học mới, ${result.updated} môn học cập nhật`, { added: result.added, updated: result.updated });
      message.success(`Đồng bộ môn học thành công: ${result.added} môn học mới, ${result.updated} môn học cập nhật`);
      
    } catch (error: any) {
      console.error('Error syncing subjects:', error);
      const errorMsg = error.response?.data?.error || 'Không thể đồng bộ môn học';
      addLog('Môn học', 'error', errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, subject: false }));
    }
  };

  // Sync Lớp học
  const handleSyncClasses = async () => {
    try {
      const values = await classForm.validateFields();
      setLoading(prev => ({ ...prev, class: true }));
      
      const response = await classService.syncClasses(values);
      if (response) {
        const { added, updated } = response;
        addLog('Lớp học', 'success', `Đồng bộ thành công: ${added} thêm mới, ${updated} cập nhật`, { added, updated });
        message.success(`Đồng bộ lớp học thành công: ${added} thêm mới, ${updated} cập nhật`);
        loadInitialData();
      }
    } catch (error: any) {
      console.error('Error syncing classes:', error);
      const errorMsg = error.response?.data?.message || 'Không thể đồng bộ lớp học';
      addLog('Lớp học', 'error', errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, class: false }));
    }
  };

  // Sync Lớp học phần (ALL)
  const handleSyncLopHocPhans = async () => {
    try {
      const values = await lopHocPhanForm.validateFields();
      setLoading(prev => ({ ...prev, lophocphan: true }));
      
      const response = await lopHocPhanService.syncAllLopHocPhan(values.KhoaHocID);
      if (response.success) {
        const { added, updated } = response;
        addLog('Lớp học phần', 'success', `Đồng bộ thành công: ${added || 0} thêm mới, ${updated || 0} cập nhật`, { added, updated });
        message.success(`Đồng bộ lớp học phần thành công: ${added || 0} thêm mới, ${updated || 0} cập nhật`);
      }
    } catch (error: any) {
      console.error('Error syncing lop hoc phans:', error);
      const errorMsg = error.response?.data?.message || 'Không thể đồng bộ lớp học phần';
      addLog('Lớp học phần', 'error', errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, lophocphan: false }));
    }
  };

  // Sync Sinh viên
  const handleSyncStudents = async () => {
    try {
      const values = await studentForm.validateFields();
      setLoading(prev => ({ ...prev, student: true }));

      const safeMode = studentSyncMode === 'safe';
      const response = await sinhVienService.sync(values.LopHocID, safeMode);
      
      if (response.success) {
        const { inserted = 0, updated = 0, deleted = 0, preserved = 0, invalidStudents = 0 } = response.data || {};
        
        let logMessage = `Đồng bộ ${safeMode ? 'an toàn' : 'toàn diện'} thành công: ${inserted} sinh viên mới, ${updated} sinh viên cập nhật`;
        if (safeMode && preserved > 0) {
          logMessage += `, ${preserved} sinh viên được bảo lưu`;
        }
        if (!safeMode && deleted > 0) {
          logMessage += `, ${deleted} sinh viên đã xóa`;
        }
        if (invalidStudents > 0) {
          logMessage += `, ${invalidStudents} sinh viên không hợp lệ bị bỏ qua`;
        }
        
        addLog('Sinh viên', 'success', logMessage, { added: inserted, updated, deleted, preserved, invalidStudents });
        message.success(logMessage);
      } else {
        addLog('Sinh viên', 'error', response.error || 'Không thể đồng bộ dữ liệu');
        message.error(response.error || 'Không thể đồng bộ dữ liệu');
      }
    } catch (error: any) {
      console.error('Error syncing students:', error);
      const errorMsg = error.response?.data?.message || 'Lỗi khi đồng bộ dữ liệu sinh viên';
      addLog('Sinh viên', 'error', errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, student: false }));
    }
  };

  // Sync Sinh viên vào Lớp học phần
  const handleSyncStudentsToLopHocPhan = async () => {
    try {
      const values = await studentLHPForm.validateFields();
      setLoading(prev => ({ ...prev, studentLHP: true }));

      const response = await sinhVienService.syncStudentsByClass(values.MaLHP);
      
      if (response.success) {
        const { count = 0, skippedMissing = 0 } = response.data || {};
        
        let logMessage = `Đồng bộ thành công: ${count} sinh viên được thêm vào lớp học phần`;
        if (skippedMissing > 0) {
          logMessage += `, ${skippedMissing} sinh viên bỏ qua (chưa có trong hệ thống)`;
        }
        
        addLog('Sinh viên (LHP)', 'success', logMessage, { added: count });
        message.success(logMessage);
      } else {
        addLog('Sinh viên (LHP)', 'error', response.error || 'Không thể đồng bộ dữ liệu');
        message.error(response.error || 'Không thể đồng bộ dữ liệu');
      }
    } catch (error: any) {
      console.error('Error syncing students to class section:', error);
      const errorMsg = error.response?.data?.message || 'Lỗi khi đồng bộ sinh viên vào lớp học phần';
      addLog('Sinh viên (LHP)', 'error', errorMsg);
      message.error(errorMsg);
    } finally {
      setLoading(prev => ({ ...prev, studentLHP: false }));
    }
  };

  // Bulk Sync Sinh viên vào tất cả Lớp học phần của một môn học
  const handleBulkSyncStudentsToLopHocPhan = async () => {
    try {
      const values = await bulkSyncForm.validateFields();
      setLoading(prev => ({ ...prev, bulkSyncLHP: true }));

      Modal.confirm({
        title: 'Xác nhận đồng bộ hàng loạt',
        icon: <ExclamationCircleOutlined />,
        content: (
          <div>
            <p>Bạn có chắc chắn muốn đồng bộ sinh viên cho <strong>TẤT CẢ lớp học phần</strong> của môn học này?</p>
            <p>Quá trình này có thể mất vài phút tùy thuộc vào số lượng lớp học phần.</p>
          </div>
        ),
        okText: 'Đồng bộ',
        cancelText: 'Hủy',
        onOk: async () => {
          message.loading('Đang đồng bộ hàng loạt...', 0);
          
          try {
            const response = await sinhVienService.bulkSyncStudentsBySubject(
              values.KhoaHocID,
              values.MonHocID
            );
            
            message.destroy();
            
            if (response.success) {
              const { totalClasses = 0, synced = 0, totalStudents = 0, details = [] } = response.data || {};
              
              const failed = totalClasses - synced;
              let logMessage = `Đồng bộ hàng loạt thành công: ${synced}/${totalClasses} lớp học phần, tổng ${totalStudents} sinh viên`;
              if (failed > 0) {
                logMessage += `, ${failed} lớp học phần thất bại`;
              }
              
              addLog('Sinh viên (Bulk)', 'success', logMessage, { added: totalStudents });
              
              // Show detailed results
              Modal.info({
                title: 'Kết quả đồng bộ hàng loạt',
                width: 600,
                content: (
                  <div>
                    <p><strong>Tổng số lớp học phần:</strong> {totalClasses}</p>
                    <p><strong>Đồng bộ thành công:</strong> {synced}</p>
                    <p><strong>Thất bại:</strong> {failed}</p>
                    <p><strong>Tổng số sinh viên:</strong> {totalStudents}</p>
                    {details.length > 0 && (
                      <div style={{ marginTop: 16, maxHeight: 300, overflow: 'auto' }}>
                        <table style={{ width: '100%', fontSize: '12px' }}>
                          <thead>
                            <tr>
                              <th>Mã LHP</th>
                              <th>Số SV</th>
                              <th>Trạng thái</th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.map((d, idx) => (
                              <tr key={idx}>
                                <td>{d.LopHocPhanID}</td>
                                <td>{d.count}</td>
                                <td>
                                  <Tag color={d.status === 'ok' ? 'green' : 'red'}>
                                    {d.status === 'ok' ? 'Thành công' : 'Lỗi'}
                                  </Tag>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )
              });
              
              message.success(logMessage);
            } else {
              addLog('Sinh viên (Bulk)', 'error', response.error || 'Không thể đồng bộ hàng loạt');
              message.error(response.error || 'Không thể đồng bộ hàng loạt');
            }
          } catch (error: any) {
            message.destroy();
            console.error('Error bulk syncing students:', error);
            const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Lỗi khi đồng bộ hàng loạt';
            addLog('Sinh viên (Bulk)', 'error', errorMsg);
            message.error(errorMsg);
          } finally {
            setLoading(prev => ({ ...prev, bulkSyncLHP: false }));
          }
        },
        onCancel: () => {
          setLoading(prev => ({ ...prev, bulkSyncLHP: false }));
        }
      });
    } catch (error: any) {
      console.error('Error in bulk sync validation:', error);
      setLoading(prev => ({ ...prev, bulkSyncLHP: false }));
    }
  };

  // Sync All (Tất cả module theo thứ tự)
  const handleSyncAll = () => {
    Modal.confirm({
      title: 'Xác nhận đồng bộ toàn bộ',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <Paragraph>
            Bạn có chắc chắn muốn đồng bộ <strong>TẤT CẢ</strong> dữ liệu từ CSDL thứ 2?
          </Paragraph>
          <Paragraph>
            Quá trình này sẽ đồng bộ theo thứ tự:
          </Paragraph>
          <ol>
            <li>Khóa học (tất cả)</li>
            <li>Môn học (theo khóa học đã chọn)</li>
            <li>Lớp học (theo khóa học đã chọn)</li>
            <li>Lớp học phần (theo khóa học đã chọn)</li>
            <li>Sinh viên (theo lớp học đã chọn)</li>
          </ol>
          <Alert
            message="Lưu ý"
            description="Quá trình này có thể mất vài phút. Vui lòng không đóng trang trong khi đồng bộ."
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        </div>
      ),
      okText: 'Đồng bộ',
      cancelText: 'Hủy',
      onOk: async () => {
        await syncAllSequentially();
      }
    });
  };

  const syncAllSequentially = async () => {
    setLoading({ course: true, subject: true, class: true, lophocphan: true, student: true });
    
    try {
      // 1. Sync Khóa học (tất cả ngành học)
      message.loading('Đang đồng bộ khóa học...', 0);
      for (const nganhHoc of nganhHocs) {
        try {
          const response = await courseService.syncCourses({ NganhHocID: nganhHoc.NganhHocID });
          if (response.success) {
            const { added, updated } = response.data;
            addLog('Khóa học', 'success', `${nganhHoc.TenNganh}: ${added} thêm mới, ${updated} cập nhật`, { added, updated });
          }
        } catch (error: any) {
          addLog('Khóa học', 'error', `${nganhHoc.TenNganh}: ${error.response?.data?.message || 'Lỗi'}`);
        }
      }
      await loadInitialData();
      message.destroy();

      // 2. Sync Môn học (nếu có khóa học được chọn)
      const selectedCourse = subjectForm.getFieldValue('KhoaHocID');
      if (selectedCourse) {
        message.loading('Đang đồng bộ môn học...', 0);
        try {
          const result = await subjectService.syncSubjects({ KhoaHocID: selectedCourse });
          addLog('Môn học', 'success', `${result.added} môn học mới, ${result.updated} môn học cập nhật`, { added: result.added, updated: result.updated });
        } catch (error: any) {
          addLog('Môn học', 'error', error.response?.data?.error || 'Không thể đồng bộ môn học');
        }
        message.destroy();
      }

      // 3. Sync Lớp học (nếu có khóa học được chọn)
      const selectedCourseForClass = classForm.getFieldValue('KhoaHocID');
      if (selectedCourseForClass) {
        message.loading('Đang đồng bộ lớp học...', 0);
        try {
          const response = await classService.syncClasses({ KhoaHocID: selectedCourseForClass });
          if (response) {
            const { added, updated } = response;
            addLog('Lớp học', 'success', `${added} thêm mới, ${updated} cập nhật`, { added, updated });
          }
        } catch (error: any) {
          addLog('Lớp học', 'error', error.response?.data?.message || 'Không thể đồng bộ lớp học');
        }
        await loadInitialData();
        message.destroy();
      }

      // 4. Sync Lớp học phần (nếu có khóa học được chọn)
      const selectedCourseForLHP = lopHocPhanForm.getFieldValue('KhoaHocID');
      if (selectedCourseForLHP) {
        message.loading('Đang đồng bộ lớp học phần...', 0);
        try {
          const response = await lopHocPhanService.syncAllLopHocPhan(selectedCourseForLHP);
          if (response.success) {
            const { added, updated } = response;
            addLog('Lớp học phần', 'success', `${added || 0} thêm mới, ${updated || 0} cập nhật`, { added, updated });
          }
        } catch (error: any) {
          addLog('Lớp học phần', 'error', error.response?.data?.message || 'Không thể đồng bộ lớp học phần');
        }
        message.destroy();
      }

      // 5. Sync Sinh viên (nếu có lớp học được chọn)
      const selectedClass = studentForm.getFieldValue('LopHocID');
      if (selectedClass) {
        message.loading('Đang đồng bộ sinh viên...', 0);
        try {
          const safeMode = studentSyncMode === 'safe';
          const response = await sinhVienService.sync(selectedClass, safeMode);
          
          if (response.success) {
            const { inserted = 0, updated = 0, deleted = 0, preserved = 0, invalidStudents = 0 } = response.data || {};
            addLog('Sinh viên', 'success', `${inserted} sinh viên mới, ${updated} sinh viên cập nhật`, { added: inserted, updated, deleted, preserved, invalidStudents });
          }
        } catch (error: any) {
          addLog('Sinh viên', 'error', error.response?.data?.message || 'Lỗi khi đồng bộ dữ liệu sinh viên');
        }
        message.destroy();
      }

      message.success('Đã hoàn thành đồng bộ toàn bộ dữ liệu!');
    } catch (error) {
      console.error('Error in sync all:', error);
      message.error('Có lỗi xảy ra trong quá trình đồng bộ');
    } finally {
      setLoading({});
    }
  };

  // Log table columns
  const logColumns: ColumnsType<SyncLog> = [
    {
      title: 'Thời gian',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 150,
    },
    {
      title: 'Module',
      dataIndex: 'module',
      key: 'module',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const config = {
          success: { icon: <CheckCircleOutlined />, color: 'success', text: 'Thành công' },
          error: { icon: <CloseCircleOutlined />, color: 'error', text: 'Lỗi' },
          warning: { icon: <ExclamationCircleOutlined />, color: 'warning', text: 'Cảnh báo' }
        };
        const current = config[status as keyof typeof config];
        return <Badge status={current.color as any} text={current.text} />;
      }
    },
    {
      title: 'Thông báo',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'Chi tiết',
      dataIndex: 'details',
      key: 'details',
      width: 200,
      render: (details: any) => {
        if (!details) return '-';
        return (
          <Space direction="vertical" size="small">
            {details.added !== undefined && <Text type="success">Thêm: {details.added}</Text>}
            {details.updated !== undefined && <Text type="warning">Cập nhật: {details.updated}</Text>}
            {details.deleted !== undefined && <Text type="danger">Xóa: {details.deleted}</Text>}
            {details.preserved !== undefined && <Text>Bảo lưu: {details.preserved}</Text>}
            {details.invalidStudents !== undefined && <Text type="secondary">Không hợp lệ: {details.invalidStudents}</Text>}
          </Space>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <div style={{ marginBottom: 24 }}>
          <Title level={3}>
            <DatabaseOutlined /> Đồng bộ dữ liệu từ CSDL thứ 2
          </Title>
          <Paragraph type="secondary">
            Trang này cho phép bạn đồng bộ dữ liệu từ cơ sở dữ liệu phụ (CSDL thứ 2) về cơ sở dữ liệu chính.
            Các module có thể đồng bộ: Khóa học, Môn học, Lớp học, Lớp học phần và Sinh viên.
          </Paragraph>
        </div>

        {/* Quick Actions */}
        <Card style={{ marginBottom: 24, background: '#f5f5f5' }}>
          <Row gutter={16}>
            <Col span={24}>
              <Space size="large" wrap>
                <Button 
                  type="primary" 
                  size="large" 
                  icon={<PlayCircleOutlined />}
                  onClick={handleSyncAll}
                  disabled={Object.values(loading).some(v => v)}
                >
                  Đồng bộ toàn bộ
                </Button>
                <Alert
                  message="Lưu ý"
                  description="Vui lòng chọn các thông số cần thiết ở các tab bên dưới trước khi đồng bộ toàn bộ"
                  type="info"
                  showIcon
                />
              </Space>
            </Col>
          </Row>
        </Card>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          {/* Tab 1: Khóa học */}
          <TabPane 
            tab={
              <span>
                <BookOutlined />
                Khóa học
              </span>
            } 
            key="1"
          >
            <Card>
              <Form form={courseForm} layout="vertical">
                <Form.Item
                  name="NganhHocID"
                  label="Chọn ngành học cần đồng bộ khóa học"
                  rules={[{ required: true, message: 'Vui lòng chọn ngành học' }]}
                >
                  <Select
                    placeholder="Chọn ngành học"
                    showSearch
                    optionFilterProp="children"
                  >
                    {nganhHocs.map(nh => (
                      <Option key={nh.NganhHocID} value={nh.NganhHocID}>
                        {nh.NganhHocID} - {nh.TenNganh}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    icon={<SyncOutlined spin={loading.course} />}
                    onClick={handleSyncCourses}
                    loading={loading.course}
                  >
                    Đồng bộ khóa học
                  </Button>
                </Form.Item>
              </Form>
              <Alert
                message="Thông tin"
                description="Đồng bộ sẽ thêm mới các khóa học chưa có và cập nhật thông tin các khóa học đã tồn tại từ CSDL phụ."
                type="info"
                showIcon
              />
            </Card>
          </TabPane>

          {/* Tab 2: Môn học */}
          <TabPane 
            tab={
              <span>
                <ReadOutlined />
                Môn học
              </span>
            } 
            key="2"
          >
            <Card>
              <Form form={subjectForm} layout="vertical">
                <Form.Item
                  name="KhoaHocID"
                  label="Chọn khóa học cần đồng bộ môn học"
                  rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
                >
                  <Select
                    placeholder="Chọn khóa học"
                    showSearch
                    optionFilterProp="children"
                  >
                    {courses.map(c => (
                      <Option key={c.KhoaHocID} value={c.KhoaHocID}>
                        {c.KhoaHocID} - {c.TenKhoaHoc}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    icon={<SyncOutlined spin={loading.subject} />}
                    onClick={handleSyncSubjects}
                    loading={loading.subject}
                  >
                    Đồng bộ môn học
                  </Button>
                </Form.Item>
              </Form>
              <Alert
                message="Thông tin"
                description="Đồng bộ sẽ thêm mới các môn học chưa có và cập nhật thông tin các môn học đã tồn tại cho khóa học đã chọn."
                type="info"
                showIcon
              />
            </Card>
          </TabPane>

          {/* Tab 3: Lớp học */}
          <TabPane 
            tab={
              <span>
                <FolderOutlined />
                Lớp học
              </span>
            } 
            key="3"
          >
            <Card>
              <Form form={classForm} layout="vertical">
                <Form.Item
                  name="KhoaHocID"
                  label="Chọn khóa học cần đồng bộ lớp học"
                  rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
                >
                  <Select
                    placeholder="Chọn khóa học"
                    showSearch
                    optionFilterProp="children"
                  >
                    {courses.map(c => (
                      <Option key={c.KhoaHocID} value={c.KhoaHocID}>
                        {c.KhoaHocID} - {c.TenKhoaHoc}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    icon={<SyncOutlined spin={loading.class} />}
                    onClick={handleSyncClasses}
                    loading={loading.class}
                  >
                    Đồng bộ lớp học
                  </Button>
                </Form.Item>
              </Form>
              <Alert
                message="Thông tin"
                description="Đồng bộ sẽ thêm mới các lớp học chưa có và cập nhật thông tin các lớp học đã tồn tại cho khóa học đã chọn."
                type="info"
                showIcon
              />
            </Card>
          </TabPane>

          {/* Tab 4: Lớp học phần */}
          <TabPane 
            tab={
              <span>
                <TeamOutlined />
                Lớp học phần
              </span>
            } 
            key="4"
          >
            <Card>
              <Form form={lopHocPhanForm} layout="vertical">
                <Form.Item
                  name="KhoaHocID"
                  label="Chọn khóa học cần đồng bộ lớp học phần"
                  rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
                >
                  <Select
                    placeholder="Chọn khóa học"
                    showSearch
                    optionFilterProp="children"
                  >
                    {courses.map(c => (
                      <Option key={c.KhoaHocID} value={c.KhoaHocID}>
                        {c.KhoaHocID} - {c.TenKhoaHoc}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    icon={<SyncOutlined spin={loading.lophocphan} />}
                    onClick={handleSyncLopHocPhans}
                    loading={loading.lophocphan}
                  >
                    Đồng bộ lớp học phần
                  </Button>
                </Form.Item>
              </Form>
              <Alert
                message="Thông tin"
                description="Đồng bộ sẽ thêm mới các lớp học phần chưa có và cập nhật thông tin các lớp học phần đã tồn tại cho khóa học đã chọn."
                type="info"
                showIcon
              />
            </Card>
          </TabPane>

          {/* Tab 5: Sinh viên */}
          <TabPane 
            tab={
              <span>
                <UserOutlined />
                Sinh viên
              </span>
            } 
            key="5"
          >
            <Card>
              <Form form={studentForm} layout="vertical">
                <Form.Item
                  name="LopHocID"
                  label="Chọn lớp học cần đồng bộ sinh viên"
                  rules={[{ required: true, message: 'Vui lòng chọn lớp học' }]}
                >
                  <Select
                    placeholder="Chọn lớp học"
                    showSearch
                    optionFilterProp="children"
                  >
                    {classes.map(c => (
                      <Option key={c.LopHocID} value={c.LopHocID}>
                        {c.LopHocID} - {c.TenLop}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Chế độ đồng bộ">
                  <Radio.Group 
                    value={studentSyncMode} 
                    onChange={(e) => setStudentSyncMode(e.target.value)}
                  >
                    <Space direction="vertical">
                      <Radio value="safe">
                        <strong>An toàn (Khuyến nghị)</strong>
                        <br />
                        <Text type="secondary">
                          Chỉ thêm mới và cập nhật. Không xóa sinh viên đã có trong CSDL chính.
                        </Text>
                      </Radio>
                      <Radio value="full">
                        <strong>Toàn diện (Nguy hiểm)</strong>
                        <br />
                        <Text type="danger">
                          Thêm, cập nhật VÀ XÓA sinh viên. Sẽ xóa sinh viên có trong CSDL chính nhưng không có trong CSDL phụ.
                        </Text>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    icon={<SyncOutlined spin={loading.student} />}
                    onClick={handleSyncStudents}
                    loading={loading.student}
                    danger={studentSyncMode === 'full'}
                  >
                    Đồng bộ sinh viên
                  </Button>
                </Form.Item>
              </Form>
              {studentSyncMode === 'full' && (
                <Alert
                  message="Cảnh báo"
                  description="Chế độ đồng bộ toàn diện có thể xóa vĩnh viễn dữ liệu sinh viên. Vui lòng sao lưu trước khi thực hiện."
                  type="error"
                  showIcon
                />
              )}
              {studentSyncMode === 'safe' && (
                <Alert
                  message="Thông tin"
                  description="Chế độ đồng bộ an toàn sẽ thêm mới và cập nhật thông tin sinh viên, không xóa các sinh viên đã có trong hệ thống chính."
                  type="info"
                  showIcon
                />
              )}
            </Card>
          </TabPane>

          {/* Tab 6: Sinh viên vào Lớp học phần */}
          <TabPane 
            tab={
              <span>
                <UsergroupAddOutlined />
                SV vào LHP
              </span>
            } 
            key="6"
          >
            <Card>
              <Title level={5}>Đồng bộ sinh viên vào Lớp học phần</Title>
              <Paragraph type="secondary">
                Chức năng này sẽ tự động thêm sinh viên từ lớp học vào lớp học phần dựa trên dữ liệu từ CSDL phụ.
              </Paragraph>

              {/* Chọn chế độ đồng bộ */}
              <Form.Item label="Chọn chế độ đồng bộ" style={{ marginBottom: 24 }}>
                <Radio.Group 
                  value={bulkSyncMode} 
                  onChange={(e) => setBulkSyncMode(e.target.value)}
                  buttonStyle="solid"
                >
                  <Radio.Button value="single">Đồng bộ từng lớp học phần</Radio.Button>
                  <Radio.Button value="bulk">Đồng bộ hàng loạt (theo môn học)</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Divider />

              {/* Chế độ đồng bộ từng lớp */}
              {bulkSyncMode === 'single' && (
                <>
                  <Title level={5}>Đồng bộ từng lớp học phần</Title>
                  <Form form={studentLHPForm} layout="vertical">
                    <Form.Item
                      name="MaLHP"
                      label="Chọn lớp học phần cần đồng bộ sinh viên"
                      rules={[{ required: true, message: 'Vui lòng chọn lớp học phần' }]}
                    >
                      <Select
                        placeholder="Chọn lớp học phần"
                        showSearch
                        optionFilterProp="children"
                      >
                        {lopHocPhans.map(lhp => (
                          <Option key={lhp.MaLHP} value={lhp.MaLHP}>
                            {lhp.MaLHP} - {lhp.TenLHP}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        icon={<SyncOutlined spin={loading.studentLHP} />}
                        onClick={handleSyncStudentsToLopHocPhan}
                        loading={loading.studentLHP}
                      >
                        Đồng bộ sinh viên vào LHP
                      </Button>
                    </Form.Item>
                  </Form>
                  <Alert
                    message="Lưu ý"
                    description={
                      <div>
                        <p>Chức năng này sẽ:</p>
                        <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                          <li>Lấy danh sách sinh viên từ lớp học tương ứng trong CSDL phụ</li>
                          <li>Tự động thêm sinh viên vào lớp học phần đã chọn</li>
                          <li>Bỏ qua các sinh viên chưa có trong hệ thống chính</li>
                          <li>Không xóa sinh viên đã có trong lớp học phần</li>
                        </ul>
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                </>
              )}

              {/* Chế độ đồng bộ hàng loạt */}
              {bulkSyncMode === 'bulk' && (
                <>
                  <Title level={5}>Đồng bộ hàng loạt theo môn học</Title>
                  <Alert
                    message="Tính năng mạnh mẽ"
                    description="Đồng bộ sinh viên cho TẤT CẢ lớp học phần của một môn học trong khóa học đã chọn. Phù hợp khi có nhiều lớp học phần cần đồng bộ."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                  <Form form={bulkSyncForm} layout="vertical">
                    <Form.Item
                      name="KhoaHocID"
                      label="Chọn khóa học"
                      rules={[{ required: true, message: 'Vui lòng chọn khóa học' }]}
                    >
                      <Select
                        placeholder="Chọn khóa học"
                        showSearch
                        optionFilterProp="children"
                        onChange={() => bulkSyncForm.resetFields(['MonHocID'])}
                      >
                        {courses.map(c => (
                          <Option key={c.KhoaHocID} value={c.KhoaHocID}>
                            {c.KhoaHocID} - {c.TenKhoaHoc}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="MonHocID"
                      label="Chọn môn học"
                      rules={[{ required: true, message: 'Vui lòng chọn môn học' }]}
                      dependencies={['KhoaHocID']}
                    >
                      <Select
                        placeholder="Chọn môn học"
                        showSearch
                        optionFilterProp="children"
                        disabled={!bulkSyncForm.getFieldValue('KhoaHocID')}
                      >
                        {subjects
                          .filter(s => s.KhoaHocID === bulkSyncForm.getFieldValue('KhoaHocID'))
                          .map(s => (
                            <Option key={s.MonHocID} value={s.MonHocID}>
                              {s.MonHocID} - {s.TenMonHoc}
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="primary"
                        icon={<SyncOutlined spin={loading.bulkSyncLHP} />}
                        onClick={handleBulkSyncStudentsToLopHocPhan}
                        loading={loading.bulkSyncLHP}
                        size="large"
                      >
                        Đồng bộ hàng loạt
                      </Button>
                    </Form.Item>
                  </Form>
                  <Alert
                    message="Thông tin đồng bộ hàng loạt"
                    description={
                      <div>
                        <p><strong>Quy trình:</strong></p>
                        <ol style={{ paddingLeft: 20, marginBottom: 0 }}>
                          <li>Tìm tất cả lớp học phần của môn học đã chọn trong khóa học</li>
                          <li>Với mỗi lớp học phần, lấy danh sách sinh viên từ CSDL phụ</li>
                          <li>Tự động thêm sinh viên vào từng lớp học phần</li>
                          <li>Báo cáo chi tiết kết quả (thành công/thất bại)</li>
                        </ol>
                        <p style={{ marginTop: 8, marginBottom: 0 }}>
                          <strong>Lưu ý:</strong> Quá trình có thể mất vài phút nếu có nhiều lớp học phần.
                        </p>
                      </div>
                    }
                    type="info"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                </>
              )}
            </Card>
          </TabPane>

          {/* Tab 7: Nhật ký đồng bộ */}
          <TabPane 
            tab={
              <span>
                <SyncOutlined />
                Nhật ký đồng bộ
              </span>
            } 
            key="7"
          >
            <Card>
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Row gutter={16}>
                  <Col span={6}>
                    <Statistic 
                      title="Tổng số lần đồng bộ" 
                      value={syncLogs.length} 
                      prefix={<SyncOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Thành công" 
                      value={syncLogs.filter(log => log.status === 'success').length} 
                      valueStyle={{ color: '#3f8600' }}
                      prefix={<CheckCircleOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Lỗi" 
                      value={syncLogs.filter(log => log.status === 'error').length} 
                      valueStyle={{ color: '#cf1322' }}
                      prefix={<CloseCircleOutlined />}
                    />
                  </Col>
                  <Col span={6}>
                    <Statistic 
                      title="Cảnh báo" 
                      value={syncLogs.filter(log => log.status === 'warning').length} 
                      valueStyle={{ color: '#faad14' }}
                      prefix={<ExclamationCircleOutlined />}
                    />
                  </Col>
                </Row>
                <Divider />
                <Table
                  columns={logColumns}
                  dataSource={syncLogs}
                  pagination={{ pageSize: 10 }}
                  size="small"
                />
              </Space>
            </Card>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default DataSyncManagementPage;
